import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Shield, 
  AlertTriangle, 
  Trash2, 
  Plus, 
  Search,
  Calendar,
  User,
  X,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApi";
import { cn } from "@/lib/utils";

interface Warning {
  id: string;
  userId: string;
  userName: string;
  userAvatar: string | null;
  moderatorId: string;
  moderatorName: string;
  moderatorAvatar: string | null;
  reason: string;
  createdAt: string;
}

interface ModerationStats {
  totalWarnings: number;
  uniqueUsers: number;
  activeModerators: number;
  recentWarnings: number;
}

export const ModerationPlugin = ({ serverId }: { serverId: string }) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddWarning, setShowAddWarning] = useState(false);
  const [newWarning, setNewWarning] = useState({ userId: "", reason: "" });

  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: async () => {
      const { pluginApi } = await import('@/lib/api');
      return pluginApi.getPlugins(serverId);
    },
  });

  const moderationPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'moderation');
  const isEnabled = moderationPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    async ({ enabled }: { enabled: boolean }) => {
      const { pluginApi } = await import('@/lib/api');
      return enabled ? pluginApi.enablePlugin(serverId, 'moderation') : pluginApi.disablePlugin(serverId, 'moderation');
    },
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  // Fetch warnings
  const { data: warnings = [], isLoading: warningsLoading } = useQuery<Warning[]>({
    queryKey: ['servers', serverId, 'moderation', 'warnings'],
    queryFn: async () => {
      const { moderationApi } = await import('@/lib/api');
      return moderationApi.getWarnings(serverId);
    },
  });

  // Fetch stats
  const { data: stats } = useQuery<ModerationStats>({
    queryKey: ['servers', serverId, 'moderation', 'stats'],
    queryFn: async () => {
      const { moderationApi } = await import('@/lib/api');
      return moderationApi.getStats(serverId);
    },
  });

  // Create warning mutation
  const createWarningMutation = useMutation({
    mutationFn: async (data: { userId: string; reason: string }) => {
      const { moderationApi } = await import('@/lib/api');
      return moderationApi.createWarning(serverId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'moderation'] });
      setShowAddWarning(false);
      setNewWarning({ userId: "", reason: "" });
    },
  });

  // Delete warning mutation
  const deleteWarningMutation = useMutation({
    mutationFn: async (warningId: string) => {
      const { moderationApi } = await import('@/lib/api');
      return moderationApi.deleteWarning(serverId, warningId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'moderation'] });
    },
  });

  const filteredWarnings = warnings.filter(warning => 
    warning.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    warning.reason?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateWarning = () => {
    if (!newWarning.userId || !newWarning.reason) return;
    createWarningMutation.mutate(newWarning);
  };

  return (
    <div className="space-y-6">      {/* Header with Toggle */}
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-500/20 rounded-xl">
            <Shield className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Moderation</h3>
            <p className="text-sm text-muted-foreground">
              Manage warnings and moderation actions.
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {isEnabled && (
      <>      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            Sistema Warning
          </h2>
          <p className="text-muted-foreground mt-1">
            Avverti gli utenti per violazioni delle regole. I warning aiutano a mantenere l'ordine senza ban immediati. Traccia la storia di moderazione di ogni utente.
          </p>
        </div>
        <Button 
          onClick={() => setShowAddWarning(true)}
          className="btn-gradient border-0"
        >
          <Plus className="w-4 h-4 mr-2" />
          Aggiungi Warning
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Warning Totali
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.totalWarnings}</div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Utenti Unici
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.uniqueUsers}</div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Moderatori Attivi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.activeModerators}</div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Ultimi 7 Giorni
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.recentWarnings}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Cerca per utente o motivo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-sidebar-accent border border-sidebar-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Warnings List */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Warning Attivi</CardTitle>
          <CardDescription>
            {filteredWarnings.length} warning{filteredWarnings.length !== 1 ? 's' : ''} trovati
          </CardDescription>
        </CardHeader>
        <CardContent>
          {warningsLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredWarnings.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                {searchTerm ? "Nessun warning trovato" : "Nessun warning presente"}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredWarnings.map((warning) => (
                <motion.div
                  key={warning.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="font-semibold">{warning.userName || 'Unknown User'}</span>
                    </div>
                    <p className="text-sm text-foreground mb-2">{warning.reason}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(warning.createdAt).toLocaleDateString('it-IT')}
                      </div>
                      <div className="flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        Moderatore: {warning.moderatorName || 'Unknown'}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteWarningMutation.mutate(warning.id)}
                    disabled={deleteWarningMutation.isPending}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Warning Modal */}
      {showAddWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Aggiungi Warning</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddWarning(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  User ID Discord
                </label>
                <input
                  type="text"
                  value={newWarning.userId}
                  onChange={(e) => setNewWarning({ ...newWarning, userId: e.target.value })}
                  placeholder="123456789012345678"
                  className="w-full px-4 py-2 bg-sidebar-accent border border-sidebar-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Motivo
                </label>
                <textarea
                  value={newWarning.reason}
                  onChange={(e) => setNewWarning({ ...newWarning, reason: e.target.value })}
                  placeholder="Descrivi il motivo del warning..."
                  rows={4}
                  className="w-full px-4 py-2 bg-sidebar-accent border border-sidebar-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleCreateWarning}
                  disabled={!newWarning.userId || !newWarning.reason || createWarningMutation.isPending}
                  className="flex-1 btn-gradient border-0"
                >
                  {createWarningMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Crea Warning"
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowAddWarning(false)}
                  className="flex-1"
                >
                  Annulla
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
      </>
      )}
    </div>
  );
};
